import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Lock, Radar, Search, Sparkles, Unlock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { downloadBlueprintPdf } from "@/lib/blueprintPdf";
import { getStripeEnvironment } from "@/lib/stripe";


type MarketplaceTrend = {
  id: string;
  title: string;
  summary: string | null;
  niche: string | null;
  tags: string[] | null;
  total_score: number | null;
  velocity_score: number | null;
  volume_score: number | null;
  relevance_score: number | null;
  published_at: string | null;
  is_free_sample: boolean;
  price_cents: number | null;
  purchased: boolean;
  unlocked: boolean;
  blueprint: Record<string, unknown> | null;
};

const scoreLabel = (n: number | null) =>
  typeof n === "number" ? Math.round(n).toString() : "—";

const priceLabel = (cents: number | null) =>
  !cents || cents <= 0 ? "$0" : `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

/** Renders whatever blueprint shape the trend carries, without assuming keys. */
const BlueprintBody = ({ blueprint }: { blueprint: Record<string, unknown> }) => {
  const entries = Object.entries(blueprint).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (!entries.length) {
    return <p className="text-sm text-muted-foreground">Blueprint is being generated.</p>;
  }
  return (
    <dl className="space-y-4">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt className="font-mono text-[11px] tracking-wider text-primary uppercase">
            {key.replace(/_/g, " ")}
          </dt>
          <dd className="mt-1 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {Array.isArray(value)
              ? value
                  .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
                  .join(" · ")
              : typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const Marketplace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openCheckout, checkoutElement } = useStripeCheckout();
  const [trends, setTrends] = useState<MarketplaceTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const [fulfilling, setFulfilling] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: rpcError } = await (supabase as any).rpc("marketplace_trends");
    if (rpcError) {
      setError("We couldn't load the marketplace just now. Please refresh in a moment.");
      setLoading(false);
      return [] as MarketplaceTrend[];
    }
    const rows = (data as MarketplaceTrend[]) ?? [];
    setTrends(rows);
    setError(null);
    setLoading(false);
    return rows;
  }, []);

  useEffect(() => {
    void load();
  }, [load, user?.id]);

  // Returning from a single-blueprint payment: fulfil it server-side right away
  // instead of waiting on the Stripe webhook, then hand over the PDF.
  useEffect(() => {
    const purchasedId = params.get("purchased");
    if (!purchasedId || !user) return;
    const sessionId = params.get("session_id");
    let cancelled = false;
    setFulfilling(purchasedId);
    (async () => {
      if (sessionId && sessionId.startsWith("cs_")) {
        await supabase.functions.invoke("fulfill-checkout", {
          body: { sessionId, environment: getStripeEnvironment() },
        });
      }
      for (let attempt = 0; attempt < 12 && !cancelled; attempt++) {

        const rows = await load();
        const match = rows.find((t) => t.id === purchasedId);
        if (match?.unlocked && match.blueprint) {
          setFulfilling(null);
          setOpenId(match.id);
          downloadBlueprintPdf({
            title: match.title,
            summary: match.summary,
            niche: match.niche,
            tags: match.tags,
            score: match.total_score,
            blueprint: match.blueprint as Record<string, unknown>,
          });
          setParams(new URLSearchParams(), { replace: true });
          return;
        }
        await new Promise((r) => setTimeout(r, 2500));
      }
      if (!cancelled) setFulfilling(null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user?.id, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return trends;
    return trends.filter((t) =>
      [t.title, t.summary, t.niche, ...(t.tags ?? [])]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [trends, query]);

  const freeCount = trends.filter((t) => t.is_free_sample).length;
  const hasLicence = trends.some((t) => t.unlocked && !t.is_free_sample && !t.purchased);

  const buy = () => {
    if (!user) {
      navigate("/auth?mode=signup&redirect=%2Fmarketplace");
      return;
    }
    openCheckout({
      priceId: "trends_discovery_lifetime_39",
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const buyBlueprint = (t: MarketplaceTrend) => {
    if (!user) {
      navigate(`/auth?mode=signup&redirect=%2Fmarketplace`);
      return;
    }
    openCheckout({
      trendId: t.id,
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/marketplace?purchased=${t.id}&session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  return (
    <>
      <Helmet>
        <title>Trend Marketplace — Browse Niches & Launch Blueprints</title>
        <meta
          name="description"
          content="Browse every AI-scored trend Trends Discovery has surfaced. Three complete launch blueprints are free to read — unlock the rest with a $39 lifetime licence."
        />
        <link rel="canonical" href="https://trendsdiscovery.com/marketplace" />
        <meta property="og:title" content="Trend Marketplace — Trends Discovery" />
        <meta
          property="og:description"
          content="Browse AI-scored trends and read three full launch blueprints for free."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trendsdiscovery.com/marketplace" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="container max-w-5xl py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
          >
            <ArrowLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <div className="mt-8 flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
              <Radar className="w-4 h-4" />
            </span>
            <span className="font-display font-semibold">Trends.Discovery</span>
          </div>

          <h1 className="mt-6 font-display text-4xl md:text-5xl font-bold">Trend marketplace</h1>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            Every trend our scanners approved, newest first — with scores, niche and tags in
            the open. {freeCount > 0 ? `${freeCount} full launch blueprints are free to read.` : "Free sample blueprints are being prepared."}{" "}
            {hasLicence
              ? "Your licence unlocks all of them."
              : "Buy any single blueprint outright, or take the $39 lifetime licence for every one of them."}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search trends, niches or tags"
                aria-label="Search trends"
                className="pl-9"
              />
            </div>
            {!hasLicence && (
              <Button variant="hero" onClick={buy}>
                Unlock everything — $39 <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>

          {!loading && !error && trends.some((t) => t.purchased) && (
            <section className="mt-10 rounded-2xl border border-primary/40 bg-primary/5 p-6">
              <h2 className="font-display text-xl font-semibold">Your purchased blueprints</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Yours to keep. Download the PDF any time — also listed on your account page.
              </p>
              <ul className="mt-4 grid gap-3">
                {trends
                  .filter((t) => t.purchased)
                  .map((t) => (
                    <li
                      key={t.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4"
                    >
                      <Link to={`/blueprint/${t.id}`} className="font-medium hover:text-primary">
                        {t.title}
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          t.blueprint &&
                          downloadBlueprintPdf({
                            title: t.title,
                            summary: t.summary,
                            niche: t.niche,
                            tags: t.tags,
                            score: t.total_score,
                            blueprint: t.blueprint as Record<string, unknown>,
                          })
                        }
                      >
                        <Download className="w-4 h-4 mr-1" /> Blueprint PDF
                      </Button>
                    </li>
                  ))}
              </ul>
            </section>
          )}

          {loading && (
            <p className="mt-12 text-sm text-muted-foreground">Loading the marketplace…</p>
          )}
          {error && <p className="mt-12 text-sm text-destructive">{error}</p>}


          {!loading && !error && filtered.length === 0 && (
            <p className="mt-12 text-sm text-muted-foreground">
              No trends match that search yet.
            </p>
          )}

          <div className="mt-10 grid gap-5">
            {filtered.map((t) => {
              const isOpen = openId === t.id;
              return (
                <article
                  key={t.id}
                  className="rounded-2xl border border-border bg-card p-6"
                  data-testid="marketplace-trend"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {t.is_free_sample ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-[10px] font-mono px-2 py-0.5">
                            <Sparkles className="w-3 h-3" /> FREE SAMPLE
                          </span>
                        ) : t.unlocked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-accent/20 text-accent text-[10px] font-mono px-2 py-0.5">
                            <Unlock className="w-3 h-3" /> UNLOCKED
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-border text-muted-foreground text-[10px] font-mono px-2 py-0.5">
                            <Lock className="w-3 h-3" /> LOCKED
                          </span>
                        )}
                        {t.niche && (
                          <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
                            {t.niche}
                          </span>
                        )}
                      </div>
                      <h2 className="mt-2 font-display text-2xl font-bold">
                        <Link to={`/blueprint/${t.id}`} className="hover:text-primary">
                          {t.title}
                        </Link>
                      </h2>
                      {t.summary && (
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                          {t.summary}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-3xl font-bold">
                        {scoreLabel(t.total_score)}
                      </div>
                      <div className="font-mono text-[10px] text-muted-foreground">SCORE</div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-muted-foreground">
                    <span>VELOCITY {scoreLabel(t.velocity_score)}</span>
                    <span>VOLUME {scoreLabel(t.volume_score)}</span>
                    <span>RELEVANCE {scoreLabel(t.relevance_score)}</span>
                    {t.published_at && (
                      <time dateTime={t.published_at}>
                        {new Date(t.published_at).toLocaleDateString()}
                      </time>
                    )}
                  </div>

                  {t.tags?.length ? (
                    <ul className="mt-3 flex flex-wrap gap-2">
                      {t.tags.map((tag) => (
                        <li
                          key={tag}
                          className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {tag}
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <span className="font-mono text-xs text-foreground">
                      {t.is_free_sample
                        ? "FREE SAMPLE · $0"
                        : t.purchased
                          ? `PURCHASED · ${priceLabel(t.price_cents)}`
                          : `${priceLabel(t.price_cents)} · OR INCLUDED IN THE $39 LIFETIME LICENCE`}
                    </span>
                    {t.unlocked && t.blueprint ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOpenId(isOpen ? null : t.id)}
                          aria-expanded={isOpen}
                        >
                          {isOpen ? "Hide launch blueprint" : "Read launch blueprint"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadBlueprintPdf({
                              title: t.title,
                              summary: t.summary,
                              niche: t.niche,
                              tags: t.tags,
                              score: t.total_score,
                              blueprint: t.blueprint as Record<string, unknown>,
                            })
                          }
                        >
                          <Download className="w-4 h-4 mr-1" /> Blueprint PDF
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="hero"
                          size="sm"
                          onClick={() => buyBlueprint(t)}
                          disabled={fulfilling === t.id}
                        >
                          {fulfilling === t.id
                            ? "Preparing your PDF…"
                            : `Buy this blueprint — ${priceLabel(t.price_cents)}`}
                        </Button>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/blueprint/${t.id}`}>See full details</Link>
                        </Button>
                        <Button variant="outline" size="sm" onClick={buy}>
                          Get all blueprints — $39
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          Instant PDF download. Buyer profile, price band, product angles and a
                          7-day launch plan.
                        </span>

                      </>
                    )}
                  </div>

                  {isOpen && t.blueprint && (
                    <div className="mt-6 border-t border-border pt-6">
                      <BlueprintBody blueprint={t.blueprint} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <div className="mt-16 rounded-2xl border border-border bg-card p-6 text-center">
            <h2 className="font-display text-2xl font-bold">
              {hasLicence ? "Your licence is active" : "Unlock every blueprint for $39"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {hasLicence ? (
                <>
                  Head to your{" "}
                  <Link to="/dashboard" className="text-primary hover:underline">
                    dashboard
                  </Link>{" "}
                  for alerts and saved trends.
                </>
              ) : (
                <>
                  One payment, lifetime access, 14-day refund. See the{" "}
                  <Link to="/roadmap" className="text-primary hover:underline">
                    public roadmap
                  </Link>{" "}
                  for what's next.
                </>
              )}
            </p>
            {!hasLicence && (
              <Button variant="hero" size="lg" className="mt-5" onClick={buy}>
                Claim lifetime access <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        {checkoutElement}
      </main>
    </>
  );
};

export default Marketplace;
